import { Helmet } from "react-helmet";
import { Route, Routes, useLocation, useParams } from "react-router-dom";
import { integrationService } from "/app/src/services";
import { Row, Col } from "antd";
import { useQuery } from "@tanstack/react-query";
import { Integration as IntegrationType, App } from "/app/src/models";
import DataPullLogs from "./logs";
import { Mappings } from "/app/src/components/generic/components/apps/data/mappings";
import Settings from "./settings";
import Test from "./test";
import Confirmation from "./confirmation";
import Manual from "./manual";
import Run from "./run";
import useSetting from "/app/src/components/settings/setting";
import { Tabs, Tab } from "@heroui/react";
import { useTranslation } from "react-i18next";
import { convertStringToBoolean } from "/app/src/helpers";

/**
 * Component for Data Pull integration. Shows settings, logs
 * @param param0 props containing the data pull app
 */
export default function DataPull({ app }: { app: App }) {
  const { integrationId } = useParams();
  const { t } = useTranslation();
  const { pathname } = useLocation();

  const { data: integration, isFetching } = useQuery({
    queryKey: ["integration", Number(integrationId)],
    queryFn: () => integrationService.getSingle(integrationId),
    enabled: Boolean(integrationId),
    initialData: { integration: {} },
    select: (data: { integration: IntegrationType }) => data.integration,
  });

  const { settingValue: enableManual } = useSetting({
    integrationId: integration?.id,
    type: "enableManual",
  });

  /**
   * Returns the currently selected tab key based on the current pathname.
   */
  const getSelectedTabKey = () => {
    return pathname;
  };

  const dataPullLinks = [
    {
      path: `/apps/${app.id}/integrations/${integration?.id}`,
      key: "settings",
    },
    {
      path: `/apps/${app.id}/integrations/${integration?.id}/tasks`,
      key: "tasks",
    },
    {
      path: `/apps/${app.id}/integrations/${integration?.id}/mappings`,
      key: "mappings",
    },
    {
      path: `/apps/${app.id}/integrations/${integration?.id}/test`,
      key: "test",
    },
    {
      path: `/apps/${app.id}/integrations/${integration?.id}/confirmation`,
      key: "confirmation",
    },
    ...(convertStringToBoolean(enableManual)
      ? [
          {
            path: `/apps/${app.id}/integrations/${integration?.id}/manual`,
            key: "manual",
          },
        ]
      : []),
    {
      path: `/apps/${app.id}/integrations/${integration?.id}/run`,
      key: "run",
    },
  ];

  return (
    <div className="integration">
      <Helmet>
        <title>{integration?.name}</title>
      </Helmet>
      {isFetching ? (
        "Loading..."
      ) : (
        <Row gutter={20}>
          <Col span={24}>
            {integration?.id && (
              <>
                <Tabs
                  selectedKey={getSelectedTabKey()}
                  color="primary"
                  classNames={{
                    tab: "bg-white px-4 py-2",
                    tabList: "bg-white flex",
                  }}
                  className="pb-[20px]"
                >
                  {dataPullLinks.map((item) => (
                    <Tab
                      key={item.path}
                      id={item.key}
                      title={t(`translation:${item.key}`, item.key)}
                      href={item.path}
                    />
                  ))}
                </Tabs>
                <Routes>
                  <Route
                    path="/"
                    element={<Settings integration={integration} />}
                  />
                  <Route
                    path="tasks"
                    element={<DataPullLogs integration={integration} />}
                  />
                  <Route
                    path="mappings"
                    element={<Mappings integration={integration} />}
                  />
                  <Route
                    path="test"
                    element={<Test integration={integration} />}
                  />
                  <Route
                    path="confirmation"
                    element={<Confirmation integration={integration} />}
                  />
                  <Route
                    path="manual"
                    element={<Manual integration={integration} />}
                  />
                  <Route
                    path="run"
                    element={<Run integration={integration} />}
                  />
                </Routes>
              </>
            )}
          </Col>
        </Row>
      )}
    </div>
  );
}
